import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Checkbox, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import { WageIncrementService } from '../../services/wageIncrement.service';
import { IApplyIncrementResult, IDueForIncrement, IncrementOutcome } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

const OUTCOME_LABELS: Record<IncrementOutcome, string> = {
    APPLIED: 'applied',
    ALREADY_INCREMENTED: 'already incremented this year',
    NO_ACTIVE_SALARY: 'no active salary',
    NO_GRADE: 'no wage grade',
    NO_INCREMENT_PERCENT: 'grade has no increment %',
    EXISTING_ROW_AT_DATE: 'already has a salary row on that date',
    NO_JOINING_DATE: 'no joining date',
};

/** "2 applied · 1 no active salary" from the per-person apply results. */
const summarise = (results: IApplyIncrementResult[]): string => {
    const counts = new Map<IncrementOutcome, number>();
    results.forEach(r => counts.set(r.outcome, (counts.get(r.outcome) ?? 0) + 1));
    return Array.from(counts.entries()).map(([o, n]) => `${n} ${OUTCOME_LABELS[o] ?? o}`).join(' · ');
};

/**
 * H-1 annual increment: the employees whose grade anniversary falls in the chosen year, with the
 * grade's increment %. Ticked people get a new salary row from "Effective from"; payroll picks it
 * up on the next run. The backend (WageIncrementController) skips anyone already done this year.
 */
const AnnualIncrements: React.FC = () => {
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [rows, setRows] = useState<IDueForIncrement[] | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [effectiveFrom, setEffectiveFrom] = useState<string>(toLocalIsoDate());
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [applying, setApplying] = useState(false);
    const [lastOutcome, setLastOutcome] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();
    const today = toLocalIsoDate();

    const load = async () => {
        if (!institutionId) return;
        try {
            const data = await WageIncrementService.getDue(institutionId, year);
            setRows(data);
            setSelected(new Set());
            setForbidden(false);
        } catch (error: any) {
            if (error?.response?.status === 403) {
                setForbidden(true);
                setRows([]);
                return;
            }
            showApiError(error, 'Failed to load employees due an increment');
        }
    };

    useEffect(() => { load(); }, [institutionId, year]);

    const toggle = (userId: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId); else next.add(userId);
            return next;
        });
    };

    const allSelected = !!rows && rows.length > 0 && selected.size === rows.length;
    const toggleAll = () => setSelected(allSelected || !rows ? new Set() : new Set(rows.map(r => r.userId)));

    const handleApply = async () => {
        if (!institutionId) return;
        setApplying(true);
        try {
            const results = await WageIncrementService.apply({ institutionId, userIds: Array.from(selected), effectiveFrom });
            const text = summarise(results);
            setLastOutcome(text);
            showSuccess(`Increment: ${text}`);
            setConfirmOpen(false);
            await load();
        } catch (error) {
            showApiError(error, 'Failed to apply the increment');
        } finally {
            setApplying(false);
        }
    };

    const effectiveInvalid = !effectiveFrom || effectiveFrom > today;

    return (
        <Box sx={{ p: 3 }} data-help-page="payroll.increments.page">
            <Typography variant="h5" sx={{ mb: 1 }}>Annual Increments</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Employees whose joining anniversary falls in the chosen year, with their wage grade's increment %.
                Tick the people to raise, pick the date the new basic starts, and apply. The next payroll run pays the new basic.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="payroll.increments.toolbar">
                {isSystemAdmin && (
                    <Box sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                        <SearchableSelect
                            data-help="payroll.increments.institution"
                            label="Select Institution"
                            options={institutions}
                            value={institutionId}
                            onChange={id => setInstitutionId(id)}
                            getOptionLabel={inst => inst.name}
                            clearable={false}
                        />
                    </Box>
                )}
                <TextField
                    label="Anniversary year" type="number" size="small" value={year}
                    onChange={(e) => { const y = Number(e.target.value); if (Number.isInteger(y) && y > 1900) setYear(y); }}
                    sx={{ flex: '1 1 140px', maxWidth: 180 }}
                    data-help="payroll.increments.year"
                />
                <TextField
                    label="Effective from" type="date" size="small" value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
                    error={effectiveInvalid}
                    helperText={effectiveInvalid ? 'Cannot be in the future' : undefined}
                    sx={{ flex: '1 1 160px', maxWidth: 200 }}
                    data-help="payroll.increments.effectiveFrom"
                />
                <Button
                    variant="contained"
                    disabled={selected.size === 0 || effectiveInvalid}
                    onClick={() => setConfirmOpen(true)}
                    data-help="payroll.increments.apply"
                >
                    Apply increment ({selected.size})
                </Button>
            </Box>

            {forbidden && <Alert severity="warning" sx={{ mb: 2 }}>You need the "Manage salary structure" permission to see and apply increments.</Alert>}
            {lastOutcome && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setLastOutcome(null)}>Last apply: {lastOutcome}</Alert>}

            {rows && rows.length === 0 && !forbidden && (
                <Alert severity="success">Nobody is due an increment in {year}. People need a wage grade with an increment %, a joining date and an active salary to show here.</Alert>
            )}

            {rows && rows.length > 0 && (
                <TableContainer component={Paper} data-help-section="payroll.increments.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={allSelected}
                                        indeterminate={selected.size > 0 && !allSelected}
                                        onChange={toggleAll}
                                        slotProps={{ input: { 'aria-label': 'Select all' } }}
                                    />
                                </TableCell>
                                <TableCell>Employee</TableCell>
                                <TableCell>Grade</TableCell>
                                <TableCell>Joined</TableCell>
                                <TableCell>Anniversary</TableCell>
                                <TableCell align="right">Current basic</TableCell>
                                <TableCell align="right">Increment %</TableCell>
                                <TableCell align="right">New basic</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.userId} hover selected={selected.has(r.userId)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={selected.has(r.userId)}
                                            onChange={() => toggle(r.userId)}
                                            slotProps={{ input: { 'aria-label': `Select ${r.userName}` } }}
                                        />
                                    </TableCell>
                                    <TableCell>{r.userName}</TableCell>
                                    <TableCell>{r.wageGradeDisplayName}</TableCell>
                                    <TableCell>{r.joiningDate}</TableCell>
                                    <TableCell>{r.anniversaryDate}</TableCell>
                                    <TableCell align="right">{formatMoney(r.currentBasic)}</TableCell>
                                    <TableCell align="right">{r.incrementPercent}%</TableCell>
                                    <TableCell align="right">{formatMoney(r.newBasic)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={confirmOpen} onClose={() => !applying && setConfirmOpen(false)}>
                <DialogTitle>Apply the annual increment?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        {selected.size} employee{selected.size === 1 ? '' : 's'} get a new salary row from {effectiveFrom} with the new basic shown.
                        From that date it replaces their current salary; other pay components stay the same. Anyone already incremented this year is skipped.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={applying}>Cancel</Button>
                    <Button variant="contained" onClick={handleApply} disabled={applying}>Apply</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AnnualIncrements;
